import React, { useRef, useState } from 'react';
import { Box, Button, TextField, Typography, IconButton, InputAdornment, Paper } from '@mui/material';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { decrypt, encrypt, encryptSHA } from '../helpers/Helpers';
import VerifyPIN from '../services/VerifyPIN';
import DecodeToken  from 'jwt-decode';


function MFA() {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const inputsRef = useRef([]);

  const handleChange = (e, idx) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return;
    const newPin = [...pin];
    newPin[idx] = value;
    setPin(newPin);
    setError('');
    if (value && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !pin[idx] && idx > 0) {
      const newPin = [...pin];
      newPin[idx - 1] = '';
      setPin(newPin);
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.some((d) => d === '')) {
      setError('El PIN debe tener 6 dígitos');
      return;
    }
    const crewCode = DecodeToken(localStorage.getItem('token'))?.data?.username;
    const key = process.env.REACT_APP_KEY || (window.REACT_APP_KEY ?? '');
    if (!crewCode || !key) {
      setError('No se encontró crewCode o clave de cifrado');
      return;
    }
    const encryptedCrewCode = encryptSHA(crewCode, key);
    const encryptedPin = encryptSHA(pin.join(''), key);

    try {
      const response = await VerifyPIN({ crewCode: encryptedCrewCode, pin: encryptedPin });
      if (response === '200') {
        setError('');
        alert('PIN verificado correctamente');
        // Aquí puedes redirigir o continuar el flujo
      } else {
        setError('PIN incorrecto o error en la verificación');
      }
    } catch (err) {
      setError('Error de conexión al verificar el PIN');
    }
  };

  const handleClickShowPin = () => setShowPin((show) => !show);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: '100vh', minHeight: '100dvh', width: '100%', boxSizing: 'border-box', px: 2, py: 4, background: 'radial-gradient(ellipse at 30% 40%, #0d1f3c 0%, #071428 60%, #040a1a 100%)', backgroundImage: 'radial-gradient(ellipse at 30% 40%, #0d1f3c 0%, #071428 60%, #040a1a 100%), radial-gradient(rgba(41, 182, 246, 0.055) 1px, transparent 1px)', backgroundSize: 'cover, 28px 28px' }}>
      <Box mb={{ xs: 3, sm: 5 }} width="100%" display="flex" justifyContent="center">
        <Box
          sx={{
            px: { xs: 1, sm: 4 },
            py: 2.5,
            display: 'inline-block',
            width: '100%',
            maxWidth: 480,
          }}
        >
          <Typography
            variant="h4"
            fontWeight={900}
            letterSpacing={2}
            textAlign="center"
            sx={{
              color: 'white',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            Proceso de MFA requerido
          </Typography>
        </Box>
      </Box>
      <Paper elevation={6} sx={{ p: { xs: 2.5, sm: 5 }, borderRadius: 4, width: '100%', maxWidth: 400, boxSizing: 'border-box', background: '#ffffff', boxShadow: '0 30px 70px rgba(0,0,0,0.5), 0 0 0 1px rgba(41,182,246,0.1)' }}>
        <Typography variant="h5" mb={3} fontWeight={700} textAlign="center" sx={{ color: '#1565c0', fontSize: { xs: '1.15rem', sm: '1.5rem' } }}>
          Ingrese su PIN de 6 dígitos
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off" display="flex" flexDirection="column" alignItems="center">
          <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 2 }} mb={2} width="100%" justifyContent="center">
            {pin.map((digit, idx) => (
              <TextField
                key={idx}
                type={showPin ? 'text' : 'password'}
                value={digit}
                onChange={e => handleChange(e, idx)}
                onKeyDown={e => handleKeyDown(e, idx)}
                inputRef={el => (inputsRef.current[idx] = el)}
                inputProps={{
                  maxLength: 1,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: {
                    width: 'clamp(28px, 9vw, 40px)',
                    height: 'clamp(36px, 11vw, 50px)',
                    fontSize: 'clamp(18px, 5.5vw, 32px)',
                    textAlign: 'center',
                    fontWeight: 700,
                    borderRadius: 10,
                    background: 'rgba(245,245,245,0.7)',
                    WebkitTextSecurity: showPin ? 'none' : 'disc',
                    transition: 'box-shadow 0.2s',
                  },
                  autoComplete: 'one-time-code',
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    boxShadow: '0 2px 8px 0 rgba(31,38,135,0.10)',
                    background: 'rgba(255,255,255,0.9)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #1976d2',
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: '2.5px solid #21cbf3',
                  },
                }}
              />
            ))}
            <IconButton onClick={handleClickShowPin} sx={{ alignSelf: 'center', ml: { xs: 0.25, sm: 1 } }} aria-label="toggle pin visibility">
              {showPin ? <MdVisibilityOff size={24} /> : <MdVisibility size={24} />}
            </IconButton>
          </Box>
          <Typography color="error" fontSize={14} mb={2} minHeight={20} textAlign="center">{error || ' '}</Typography>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={pin.some(d => d === '')}
            sx={{
              py: 1.5,
              fontWeight: 700,
              fontSize: 18,
              borderRadius: 3,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
              background: pin.some(d => d === '')
                ? 'linear-gradient(90deg, #bdbdbd 0%, #e0e0e0 100%)'
                : 'linear-gradient(90deg, #1976d2 0%, #21cbf3 100%)',
              color: pin.some(d => d === '') ? '#757575' : 'white',
              transition: 'background 0.3s',
              '&:hover': {
                background: pin.some(d => d === '')
                  ? 'linear-gradient(90deg, #bdbdbd 0%, #e0e0e0 100%)'
                  : 'linear-gradient(90deg, #1565c0 0%, #00bcd4 100%)',
              },
              cursor: pin.some(d => d === '') ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{margin:0}}>Verificar PIN</span>
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default MFA;